import { FaFilePdf, FaTrashAlt, FaCloudUploadAlt } from 'react-icons/fa';
import { useVideoEditMaterialsSection } from './useVideoEditMaterialsSection';
import { VideoEditMaterialsSectionProps, MAX_MATERIALS_PER_VIDEO } from './types';
import { MATERIAL_UPLOAD_INTERNAL_ONLY } from '../../constants/accessMessages';
import styles from './VideoEditMaterialsSection.module.css';

const PDF_INPUT_ACCEPT = 'application/pdf,.pdf';

/**
 * 動画編集ページの資料（PDF）セクション。
 * 1動画につき最大3件まで。追加・削除はその場で確定する（保存ボタン不要）。
 */
const VideoEditMaterialsSection = (props: VideoEditMaterialsSectionProps) => {
  const { isVideoProcessing } = props;

  const {
    materials,
    isLoading,
    isUploading,
    fileInputRef,
    isDragging,
    isExternalNetwork,
    handleFileButtonClick,
    handleFileInputChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDelete,
    formatFileSize,
  } = useVideoEditMaterialsSection(props);

  if (isVideoProcessing) {
    return (
      <div className={`${styles['video-edit-materials-section']} ${styles['video-edit-materials-section--disabled']}`} data-testid="video-edit-materials-section">
        <p className={styles['video-edit-materials-section__processing-text']}>
          資料は変換完了後に編集できます
        </p>
      </div>
    );
  }

  const canAddMore = materials.length < MAX_MATERIALS_PER_VIDEO;

  return (
    <div className={styles['video-edit-materials-section']} data-testid="video-edit-materials-section">
      {isLoading ? (
        <p className={styles['video-edit-materials-section__loading']} data-testid="video-edit-materials-section__loading">
          資料を読み込み中...
        </p>
      ) : materials.length > 0 ? (
        <ul className={styles['video-edit-materials-section__list']} data-testid="video-edit-materials-section__list">
          {materials.map((material, index) => (
            <li key={material.id} className={styles['video-edit-materials-section__item']} data-testid={`video-edit-materials-section__item-${index}`}>
              <FaFilePdf className={styles['video-edit-materials-section__item-icon']} />
              <span className={styles['video-edit-materials-section__item-name']} title={material.original_filename}>
                {material.original_filename}
              </span>
              <span className={styles['video-edit-materials-section__item-size']}>
                {formatFileSize(material.file_size)}
              </span>
              <button
                type="button"
                className={styles['video-edit-materials-section__delete-button']}
                onClick={() => handleDelete(material.id)}
                disabled={isExternalNetwork}
                aria-disabled={isExternalNetwork}
                title={isExternalNetwork ? MATERIAL_UPLOAD_INTERNAL_ONLY : undefined}
                aria-label={`${material.original_filename}を削除`}
                data-testid={`video-edit-materials-section__delete-button-${index}`}
              >
                <FaTrashAlt />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles['video-edit-materials-section__empty']} data-testid="video-edit-materials-section__empty">
          この動画に資料はまだ登録されていません。
        </p>
      )}

      {canAddMore && (
        <div
          className={`${styles['video-edit-materials-section__dropzone']} ${isDragging ? styles['dragging'] : ''} ${isUploading ? styles['uploading'] : ''} ${isExternalNetwork ? styles['disabled'] : ''}`}
          onDragOver={isExternalNetwork ? undefined : handleDragOver}
          onDragLeave={isExternalNetwork ? undefined : handleDragLeave}
          onDrop={isExternalNetwork ? undefined : handleDrop}
          onClick={isUploading || isExternalNetwork ? undefined : handleFileButtonClick}
          title={isExternalNetwork ? MATERIAL_UPLOAD_INTERNAL_ONLY : undefined}
          aria-disabled={isExternalNetwork}
          data-testid="video-edit-materials-section__dropzone"
        >
          <FaCloudUploadAlt className={styles['video-edit-materials-section__dropzone-icon']} />
          <p className={styles['video-edit-materials-section__dropzone-text']}>
            {isExternalNetwork ? (
              MATERIAL_UPLOAD_INTERNAL_ONLY
            ) : isUploading ? (
              '資料をアップロード中...'
            ) : (
              <>
                クリックまたはドラッグ&ドロップでPDFを追加
                <br />
                <small>最大{MAX_MATERIALS_PER_VIDEO}件・1件100MBまで</small>
              </>
            )}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={PDF_INPUT_ACCEPT}
            onChange={handleFileInputChange}
            disabled={isExternalNetwork}
            className={styles['video-edit-materials-section__file-input']}
            aria-label="資料ファイルを選択"
            data-testid="video-edit-materials-section__file-input"
          />
        </div>
      )}
    </div>
  );
};

export default VideoEditMaterialsSection;
